import { type ReactNode, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Card, PageHeader } from "../ui";
import { SectionHeader } from "./SectionHeader";

/** The six headings of the page, in order. Other pages link to a heading by
 *  its id (`/help/3d-library#own-model`). */
export const HELP_SECTIONS = [
  { id: "what", title: "What the Objects section is" },
  { id: "kinds", title: "Objects, surfaces, artwork and looks" },
  { id: "own-model", title: "Bringing your own model" },
  { id: "areas", title: "Label and screen areas" },
  { id: "credits", title: "What costs credits and what does not" },
  { id: "rights", title: "Your rights and ours" },
] as const;

type SectionId = (typeof HELP_SECTIONS)[number]["id"];

/** What each action costs, in the user's words. Prices that are settings are
 *  not repeated here: the screens that charge show them. */
const COSTS: [string, string][] = [
  [
    "Browsing, the 3D view, dressing an object, saving a look, using your own picture as artwork, editing details, favourites, choosing objects for an ad",
    "Free",
  ],
  [
    "Using library objects in an ad",
    "Nothing extra: you pay for rendering the ad, as always",
  ],
  ["Importing a model and having it checked", "Free"],
  ["Saving objects from an ad", "Free"],
  [
    "Changing the artwork printed on an object in an ad, or swapping an object in an ad",
    "A new preview render",
  ],
  [
    "An instruction in the design studio",
    "Credits for the work done; nothing when it could not be done",
  ],
  [
    "Tuning a design with its sliders, changing its surfaces, going back to an earlier step, adding it to Objects",
    "Free",
  ],
  [
    "True render",
    "By the second of rendering; the price is shown beside the button",
  ],
  [
    "Generated artwork and sculpted designs",
    "A fixed price each, shown before you start",
  ],
];

const P = ({ children }: { children: ReactNode }) => (
  <p className="m-0 text-slate-700">{children}</p>
);

const BODY: Record<SectionId, ReactNode> = {
  what: (
    <>
      <P>
        The Objects section holds ready-made 3D objects, the surfaces they can
        be made of, and artwork for their labels and screens. When you describe
        an ad, TrueReel looks here first and uses what fits, at its real size,
        instead of modelling everything from nothing. You can also choose
        objects yourself: open one, dress it, and press Use in a new ad.
      </P>
      <P>
        Everything TrueReel provides here may be used in the ads you make. What
        you add yourself is marked Yours, and only your organisation sees it.
      </P>
    </>
  ),
  kinds: (
    <>
      <P>
        <strong>Object.</strong> A 3D model at its real size: a phone, a bottle,
        a chair. An object has named surfaces (Body, Screen, Label), and some
        have moving parts, such as a laptop&apos;s lid.
      </P>
      <P>
        <strong>Surface.</strong> What part of an object is made of: brushed
        aluminium, oak, frosted glass. Many surfaces can take a colour of your
        choice.
      </P>
      <P>
        <strong>Artwork.</strong> A picture that goes on a label or screen area:
        your app&apos;s screen, your bottle&apos;s label.
      </P>
      <P>
        <strong>Look.</strong> An object dressed your way and saved under a
        name. Save this look keeps your choice of surfaces, colours and artwork.
        Your looks are listed under Yours.
      </P>
      <P>
        <strong>Favourite.</strong> A star on anything you use often. TrueReel
        prefers your organisation&apos;s favourites when it writes an ad.
      </P>
    </>
  ),
  "own-model": (
    <>
      <P>
        Use Import in Objects to add a model of your own product. If you do not
        see Import, it is not switched on for your account yet.
      </P>
      <P>
        The safest file is a single .glb with its textures inside. To make one
        in Blender: select your model; apply its scale (Object → Apply → All
        Transforms) so that it has its real size in metres; choose File → Export
        → glTF 2.0; set Format to glTF Binary (.glb); under Include tick
        Selected Objects; under Mesh tick Apply Modifiers; leave Images on
        Automatic, which puts the textures inside the file; untick Animation.
      </P>
      <P>
        Limits: a file of up to 100 MB (the Import page lists any other file
        types it accepts); up to 2 million triangles (models over 150,000 are
        simplified for you); up to 8 materials; pictures inside the file must be
        PNG, JPEG or WebP, no larger than 8,192 pixels a side. Cameras, lights
        and animation in the file are ignored.
      </P>
      <P>
        After the upload TrueReel checks the model and shows you what it found.
        You set its real height, which side is the front, and what each surface
        is made of, then add it to Objects.
      </P>
    </>
  ),
  areas: (
    <>
      <P>
        Some objects have areas made for your artwork: a phone&apos;s screen, a
        bottle&apos;s label, the front of a box. Each area has a size in
        millimetres and a shape, meaning how wide it is compared with how tall.
      </P>
      <P>
        A picture with the same shape fits exactly. A picture up to a quarter
        wider or taller is used with its edges cropped evenly. Anything further
        off cannot go on that area.
      </P>
      <P>
        To put your picture on an object: open the object, select the area under
        Surfaces, press Choose artwork, and pick or upload a picture under Your
        images. The window tells you the size to prepare. A label that wraps
        around a bottle or a can is a flat picture too; its left and right edges
        meet at the back.
      </P>
      <P>
        When an ad needs words on a label or a screen, TrueReel typesets them
        from your brief. Upload your own picture when you want your exact
        design.
      </P>
      <P>
        To change a label or screen in an ad you already made: if TrueReel
        typeset it, describe the change in the box under the preview on the
        ad&apos;s page. If it is your own picture, save a look with the new
        picture, then name that look in the same box (&quot;Use My can v2 in the
        scene.&quot;). Either way the ad needs a new preview.
      </P>
    </>
  ),
  credits: (
    <>
      <table className="w-full border-collapse text-sm text-slate-700 sm:text-base">
        <tbody>
          {COSTS.map(([what, cost]) => (
            <tr key={what} className="border-b border-slate-200 align-top">
              <th scope="row" className="py-2 pr-4 text-left font-normal">
                {what}
              </th>
              <td className="w-2/5 py-2 font-semibold text-slate-900">
                {cost}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <P>
        Every action that costs credits shows its price before you press the
        button.
      </P>
    </>
  ),
  rights: (
    <>
      <P>
        When you import a model or upload a picture, you confirm that you may
        use it in advertising. Models that require a credit line cannot be used:
        an ad cannot carry a credit.
      </P>
      <P>
        What you import, design, save or buy stays private to your organisation
        and is never shared with other customers. TrueReel does not sell models
        and takes no rights in yours. If someone tells us that an object
        infringes their rights, we may remove it.
      </P>
    </>
  ),
};

/** `/help/3d-library`: what the 3D library is, in six headings. Static: no
 *  request and no state. */
export function HelpPage() {
  const { hash } = useLocation();
  // A link from inside the app changes the address without loading a
  // document, so the browser neither scrolls to the heading the address names
  // (`…#own-model`) nor back to the top of a page reached from far down
  // another one (the job page's "What is this?").
  useEffect(() => {
    const target = hash
      ? document.getElementById(hash.slice(1))
      : document.documentElement;
    target?.scrollIntoView?.();
  }, [hash]);
  return (
    <>
      <PageHeader eyebrow="— Objects" title="Help" />
      <SectionHeader />
      <Card className="flex max-w-3xl flex-col gap-10 p-6 sm:p-8">
        {HELP_SECTIONS.map(({ id, title }) => (
          <section key={id} className="flex flex-col gap-3">
            <h2 id={id} className="tb-h-row m-0 scroll-mt-24">
              {title}
            </h2>
            {BODY[id]}
          </section>
        ))}
      </Card>
    </>
  );
}
